import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { addPaymentMethod as addPaymentMethodService } from '../../services/subscriptionService'; // Renamed to avoid conflict
// import { useAddPaymentMethod } from '../../services/subscriptionService'; // Renamed to avoid conflict
import { StripeCardElementOptions } from '@stripe/stripe-js';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
// import { useQuery, useQueryClient, QueryClient } from '@tanstack/react-query';

// Basic styling for CardElement (optional, can be customized)
const cardElementOptions: StripeCardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

interface PaymentMethodFormProps {
  onSuccess?: () => void; // Callback on successful addition
  onError?: (error: Error) => void; // Callback on error
}

const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({ onSuccess, onError }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({ name: '', lastName: '' });
  // const { mutate: addPaymentMethod } = useAddPaymentMethod();
  // const { mutate } = useAddPaymentMethodMutation(); // If using react-query for mutations

//   const handleSubmit = async (event: React.FormEvent) => {
//   event.preventDefault();
//   setError(null);

//   if (!stripe || !elements) {
//     const message = 'Stripe.js has not loaded yet.';
//     setError(message);
//     onError?.(new Error(message));
//     return;
//   }

//   const cardElement = elements.getElement(CardElement);
//   if (!cardElement) {
//     const message = 'Card element not found.';
//     setError(message);
//     onError?.(new Error(message));
//     return;
//   }

//   if (!formData.name.trim() || !formData.lastName.trim()) {
//     return;
//   }

//   const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
//     type: 'card',
//     card: cardElement,
//     billing_details: {
//       name: `${formData.name} ${formData.lastName}`,
//     },
//   });

//   if (stripeError || !paymentMethod || !paymentMethod.card) {
//     const message = stripeError?.message || 'Error creando el método de pago.';
//     setError(message);
//     onError?.(new Error(message));
//     return;
//   }

//   // ✅ Llama a mutate
//   addPaymentMethod(
//     {
//       stripePaymentMethodId: paymentMethod.id,
//       cardBrand: paymentMethod.card.brand,
//       last4: paymentMethod.card.last4,
//       expMonth: paymentMethod.card.exp_month,
//       expYear: paymentMethod.card.exp_year,
//     },
//     {
//       onSuccess: () => {
//         cardElement.clear();
//         setFormData({ name: '', lastName: '' });
//         onSuccess?.();
//       },
//       onError: (err: any) => {
//         const message =
//           err?.response?.data?.message || err?.message || 'Unexpected payment method error';
//         setError(message);
//         console.error('Mutation error:', message);
//         onError?.(new Error(message));
//       },
//     }
//   );
// };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    if (!stripe || !elements) {
      const message = t('stripeNotLoaded');
      setError(message);
      setLoading(false);
      if (onError) onError(new Error(message));
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      const message = t('cardElementNotFound');
      setError(message);
      setLoading(false);
      if (onError) onError(new Error(message));
      return;
    }

    if(!formData.name.trim() || !formData.lastName.trim()){
      toast.error(t('nameAndLastNameRequired'));
      setLoading(false);
      return;
    }

    try {
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: `${formData.name} ${formData.lastName}`,
          // email: 'test@example.com' // Optional: include user's email if available
        },
      });

      if (stripeError || !paymentMethod) {
        const message = stripeError?.message || t('failedToCreatePaymentMethod');
        setError(message);
        if (onError && stripeError) onError(stripeError);
        else if (onError) onError(new Error(message));
        setLoading(false);
        return;
      }

      if (!paymentMethod.card) {
         const message = t('errorNoCardInfo');
         setError(message);
         if (onError) onError(new Error(message));
         setLoading(false);
         return;
      }
      // Call your backend service
      await addPaymentMethodService({
        stripePaymentMethodId: paymentMethod.id,
        cardBrand: paymentMethod.card.brand,
        last4: paymentMethod.card.last4,
        expMonth: paymentMethod.card.exp_month,
        expYear: paymentMethod.card.exp_year,
        // isDefault: true, // Optional: decide if new cards should be default
      });

      // mutate( { paymentMethod: formattedPaymentMethod } ); // if using react-query

      setLoading(false);
      toast.success(t('paymentMethodAddedSuccessfully')); // Replace with better notification
      cardElement.clear(); // Clear card details on success
      setFormData({ name: '', lastName: '' }); // Clear name fields
      if (onSuccess) onSuccess();

    } catch (err: any) {
      const message =
        err?.response?.data?.message || err?.message || t('unexpectedPaymentMethodError');
      console.error('Add PM Error:', message);
      setError(message);
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4 border rounded-lg shadow">
      <div>
        <label htmlFor="cardholderName" className="block text-sm font-medium text-gray-700">{t('name')}</label>
        <input
          type="text"
          id="cardholderName"
          placeholder={t('namePlaceholder')}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />
      </div>
      <div>
        <label htmlFor="cardholderLastName" className="block text-sm font-medium text-gray-700">{t('lastName')}</label>
        <input
          type="text"
          id="cardholderLastName"
          placeholder={t('lastNamePlaceholder')}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          value={formData.lastName}
          onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">{t('cardDetails')}</label>
        <div className="mt-1 p-3 border border-gray-300 rounded-md">
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      {error && <div className="text-red-500 text-sm">{error}</div>}

      <button
        type="submit"
        disabled={!stripe || loading}
        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
      >
        {loading ? t('adding') : t('addPaymentMethod')}
      </button>
    </form>
  );
};

export default PaymentMethodForm;
